import React from 'react';
import { Button } from '@cowbell/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil } from '@fortawesome/pro-regular-svg-icons';
import { useSchemaMap } from './SchemaMapContext';
import { EndorsementModal } from './EndorsementModal';
import type { CoverageValue } from './EndorsementModal';
import type { RaterRulesByField, SchemaNode } from './types';

interface EndorsementEditAffordanceProps {
  readonly fieldKey?: string;
  readonly title: string;
  readonly isSelected: boolean;
  readonly opened: boolean;
  readonly existingProps?: Record<string, unknown>;
  readonly rules?: RaterRulesByField;
  readonly onOpenChange: (opened: boolean) => void;
  readonly onSave: (value: CoverageValue) => void;
}

// Edit affordance for an excess amendatory coverage. Gated SOLELY by schema
// presence: shows iff the excess schema map has an entry for the coverage's
// field key (no-input statics have no schema => nothing renders, and they attach
// via the normal selection toggle). Inert on primary (empty schema map context).
//
// The modal is controlled by the parent so that selecting a payload-bearing
// coverage can open it WITHOUT dispatching to the rater; the dispatch is
// deferred until the user saves.
export function EndorsementEditAffordance({
  fieldKey,
  title,
  isSelected,
  opened,
  existingProps,
  rules,
  onOpenChange,
  onSave,
}: EndorsementEditAffordanceProps) {
  const schemaMap = useSchemaMap();
  const schema = fieldKey
    ? (schemaMap[fieldKey] as SchemaNode | undefined)
    : undefined;

  if (!schema) {
    return null;
  }

  const hasSavedInputs =
    existingProps !== undefined && Object.keys(existingProps).length > 0;
  const actionLabel = hasSavedInputs ? 'Edit' : 'Add';

  return (
    <>
      <Button
        size="sm"
        variant="light"
        leftSection={<FontAwesomeIcon icon={faPencil} />}
        disabled={!isSelected}
        aria-label={`${actionLabel} ${title}`}
        onClick={() => onOpenChange(true)}
      >
        {actionLabel}
      </Button>
      <EndorsementModal
        opened={opened}
        title={title}
        schema={schema}
        rules={rules}
        existingProps={existingProps}
        onClose={() => onOpenChange(false)}
        onSave={onSave}
      />
    </>
  );
}
